import { api } from "../services/api";
import { setLoadingBar } from "./siteData";
import {
  ModeIds,
  VariantTypes,
  MODE_ID_PRACTICE_CHESS,
  MODE_ID_PRACTICE_CHESS960,
} from "@chesshotel/constants";
import { GamesArchive } from "@chesshotel/models";
import { ResultTypes } from "@chesshotel/constants";

import { createReduxModule } from "hooks-for-redux";
import { userStore } from "./user";

const initialState = {
  chess: {
    elo: 1200,
    nrGames: 0,
    winsBlack: 0,
    winsWhite: 0,
    lossesBlack: 0,
    lossesWhite: 0,
    drawsBlack: 0,
    drawsWhite: 0,
  },
  chess960: {
    elo: 1200,
    nrGames: 0,
    winsBlack: 0,
    winsWhite: 0,
    lossesBlack: 0,
    lossesWhite: 0,
    drawsBlack: 0,
    drawsWhite: 0,
  },
  cachedGames: {
    [ModeIds.BULLET]: [],
    [ModeIds.BLITZ]: [],
    [ModeIds.RAPID]: [],
    [ModeIds.CHESS960]: [],
    [ModeIds.DAILY]: [],
    [MODE_ID_PRACTICE_CHESS]: [],
    [MODE_ID_PRACTICE_CHESS960]: [],
  },
  totalGames: {
    [ModeIds.BULLET]: 0,
    [ModeIds.BLITZ]: 0,
    [ModeIds.RAPID]: 0,
    [ModeIds.CHESS960]: 0,
    [ModeIds.DAILY]: 0,
    [MODE_ID_PRACTICE_CHESS]: 0,
    [MODE_ID_PRACTICE_CHESS960]: 0,
  },
  viewedGames: [],
  gamesLoaded: false,
};
const [
  useStats,
  { setStats, resetStats, updateStats, setGames, setGamesLoaded },
  statsStore,
] = createReduxModule("stats", initialState, {
  setStats: (state, stats) => ({
    ...state,
    chess: stats.all,
    chess960: stats.chess960,
  }),
  resetStats: () => initialState,
  updateStats: (state, { result, playingWhite, userElo, variantId }) => {
    const resultKey = getResultKey(result, playingWhite);

    if (variantId === VariantTypes.VARIANT_CHESS) {
      return {
        ...state,
        chess: {
          ...state.chess,
          elo: userElo,
          [resultKey]: state.chess[resultKey] + 1,
          nrGames: state.chess.nrGames + 1,
        },
      };
    } else {
      return {
        ...state,
        chess960: {
          ...state.chess960,
          elo: userElo,
          [resultKey]: state.chess960[resultKey] + 1,
          nrGames: state.chess960.nrGames + 1,
        },
      };
    }
  },
  setGames: (state, { page, modeId, games, totalGames }) => {
    const rows = 10;
    const start = page * rows;
    const cached = state.cachedGames[modeId];
    for (let i = 0; i < rows; i++) {
      cached[start + i] = games[i];
    }

    return {
      ...state,
      cachedGames: { ...state.cachedGames, [modeId]: cached },
      viewedGames: games,
      totalGames: { ...state.totalGames, [modeId]: totalGames },
      gamesLoaded: true,
    };
  },
  setGamesLoaded: (state, gamesLoaded) => ({
    ...state,
    gamesLoaded,
  }),
});

export { useStats, setStats, resetStats, updateStats, setGames, statsStore };

function getResultKey(result: ResultTypes, playingWhite: boolean) {
  switch (result) {
    case ResultTypes.WHITE_WINNS:
      return playingWhite ? "winsWhite" : "winsBlack";
    case ResultTypes.BLACK_WINNS:
      return playingWhite ? "lossesWhite" : "lossesBlack";
    case ResultTypes.DRAW:
      return playingWhite ? "drawsWhite" : "drawsBlack";
    default:
      return "error";
  }
}

export interface PlayedGame extends GamesArchive {
  playingWhite: boolean;
  positions: string[];
  moves: any[];
  opponentName: string;
  opponentId: number;
  userOldElo: number;
  userNewElo: number;
  opponentOldElo: number;
  opponentNewElo: number;
}

export function getGames(page: number, modeId: any, tier) {
  const rows = 10;

  // TODO: cache games
  // if (getState().stats.cachedGames[modeId][start] && getState().stats.cachedGames[modeId][start + rows - 1]) {
  //   dispatch(setCachedGames(page, rows, modeId))
  //   return;
  // }

  setLoadingBar(true);
  setGamesLoaded(false);

  let leagueGame, variantId, timeTypeId;
  switch (modeId) {
    case ModeIds.BLITZ:
      leagueGame = 1;
      variantId = VariantTypes.VARIANT_CHESS;
      timeTypeId = ModeIds.BLITZ;
      break;
    case ModeIds.BULLET:
      leagueGame = 1;
      variantId = VariantTypes.VARIANT_CHESS;
      timeTypeId = ModeIds.BULLET;
      break;
    case ModeIds.RAPID:
      leagueGame = 1;
      variantId = VariantTypes.VARIANT_CHESS;
      timeTypeId = ModeIds.RAPID;
      break;
    case ModeIds.CHESS960:
      leagueGame = 1;
      variantId = VariantTypes.VARIANT_CHESS960;
      timeTypeId = -1;
      break;
    case ModeIds.DAILY:
      leagueGame = 1;
      variantId = -1;
      timeTypeId = ModeIds.DAILY;
      break;

    case MODE_ID_PRACTICE_CHESS:
      leagueGame = 0;
      variantId = VariantTypes.VARIANT_CHESS;
      timeTypeId = -1;
      break;
    case MODE_ID_PRACTICE_CHESS960:
      leagueGame = 0;
      variantId = VariantTypes.VARIANT_CHESS960;
      timeTypeId = -1;
      break;

    default:
      return;
  }

  import("@chesshotel/chesslib").then((module) => {
    const { Chess } = module;

    api
      .post("/games-archive", {
        page,
        rows,
        leagueGame,
        variantId,
        timeTypeId,
        tier,
      })
      .then((response) => {
        const userId = userStore.getState().id;
        const gameData: GamesArchive[] = response.data.games;
        console.log({ gameData });

        const games: PlayedGame[] = [];
        for (let i = 0; i < gameData.length; i++) {
          const game = gameData[i];

          const chess = Chess();
          let firstPos: string;
          if (game.variantId === VariantTypes.VARIANT_CHESS960) {
            firstPos = game.startFen;
            if (game.startFen != null) {
              chess.load(game.startFen, game.variantId);
            } else {
              continue;
            }
          } else {
            firstPos = chess.fen();
          }

          const movesArr = game.pgn.split(" ");
          const positions = movesArr.map((move) => {
            chess.move(move);
            return chess.fen();
          });
          positions.unshift(firstPos);
          const moves = chess.history({ verbose: true });
          const playingWhite = userId === game.whiteId;
          const opponentName = playingWhite
            ? game.blackUsername
            : game.whiteUsername;
          const opponentId = playingWhite ? game.blackId : game.whiteId;

          const userOldElo = playingWhite ? game.whiteOldElo : game.blackOldElo;
          const userNewElo = playingWhite ? game.whiteNewElo : game.blackNewElo;

          const opponentOldElo = playingWhite
            ? game.blackOldElo
            : game.whiteOldElo;
          const opponentNewElo = playingWhite
            ? game.blackNewElo
            : game.whiteNewElo;
          const playedGame = {
            ...game,
            playingWhite,
            positions,
            moves,
            opponentName,
            userOldElo,
            userNewElo,
            opponentOldElo,
            opponentNewElo,
            opponentId,
          };
          games.push(playedGame);
        }

        setGames({ games, totalGames: response.data.totalGames, page, modeId });
        setLoadingBar(false);
      })
      .catch((err) => {
        console.log("get games failed", err);
        setLoadingBar(false);
        setGamesLoaded(true);
      });
  });
}
